import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { StageTotalObj } from 'common/models/Reporting/BusinessIntelligence';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import SettingsIcon from '@mui/icons-material/Settings';
import { NameIdObj } from 'common/models/GenericTypes';
import { GetContractorBookByQuantity } from 'services/BusinessIntelligence';
import { BasicFilter } from 'common/models/Reporting/AnalyticsFilterData';

interface Props {
  id: string;
  title: string;
  sourceType: string;
  selectedIds: string;
  showConfigButton?: boolean;
  kioskToken?: string | null;
  refreshFlag?: boolean | null;
  saveConfigHandler?: (
    id: string,
    title: string,
    userFilterIds: string,
    sourceType: string
  ) => void;
}
export default function BarGraphStageTotal({
  id,
  title: titleProp,
  sourceType: sourceTypeProp,
  selectedIds,
  showConfigButton,
  kioskToken = null,
  refreshFlag = null,
  saveConfigHandler,
}: Props) {
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState('currentuser');
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [data, setData] = useState<StageTotalObj[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorHandler, setErrorHandler] = useState('');
  const t = useTheme();

  const previousProps = useRef({
    titleProp: titleProp,
    sourceTypeProp: sourceTypeProp,
    selectedIds: selectedIds,
    showConfigDialog: showConfigDialog,
    kioskToken: kioskToken,
  });

  const getData = async () => {
    setIsLoading(true);
    let filterTypeId = 0;
    if (sourceTypeProp == 'consultants') {
      filterTypeId = 4;
    } else if (sourceTypeProp == 'groups') {
      filterTypeId = 3;
    } else if (sourceTypeProp == 'currentuser') {
      filterTypeId = 5;
    } else {
      filterTypeId = 1;
    }
    const filter = { userFilterID: selectedIds, userFilterTypeID: filterTypeId } as BasicFilter;
    const currentContractorsRes = await GetContractorBookByQuantity(
      filter,
      kioskToken,
      setErrorHandler
    );
    if (currentContractorsRes) setData(currentContractorsRes);

    setIsLoading(false);
  };

  useEffect(() => {
    if (
      previousProps.current.titleProp !== titleProp ||
      previousProps.current.sourceTypeProp !== sourceTypeProp ||
      previousProps.current.selectedIds !== selectedIds ||
      previousProps.current.showConfigDialog !== showConfigDialog ||
      previousProps.current.kioskToken !== kioskToken
    ) {
      setTitle(titleProp);
      setSourceType(sourceTypeProp);
      if (selectedIds) {
        if (sourceTypeProp == 'consultants') {
          setSelectedUsers(selectedIds.split(',').map(Number));
        }

        if (sourceTypeProp == 'groups') {
          setSelectedGroups(selectedIds.split(',').map(Number));
        }
      }

      !showConfigDialog && getData();

      previousProps.current = {
        titleProp: titleProp,
        sourceTypeProp: sourceTypeProp,
        selectedIds: selectedIds,
        showConfigDialog: showConfigDialog,
        kioskToken: kioskToken,
      };
    }
  }, [
    sourceTypeProp,
    selectedIds,
    titleProp,
    showConfigDialog,
    kioskToken,
    setTitle,
    setSourceType,
    setSelectedUsers,
    setSelectedGroups,
    getData,
  ]);
  useEffect(() => {
    getData();
  }, [refreshFlag, kioskToken]);

  const mapIdsToString = useCallback(() => {
    let selectedIds = '';
    if (sourceType == 'consultants') {
      selectedIds = selectedUsers.join(',');
    } else if (sourceType == 'groups') {
      selectedIds = selectedGroups.join(',');
    }
    return selectedIds;
  }, [selectedUsers, selectedGroups, sourceType]);

  const onSaveConfig = useCallback(() => {
    saveConfigHandler && saveConfigHandler(id, title, mapIdsToString(), sourceType);
    setShowConfigDialog(false);
  }, [saveConfigHandler, selectedUsers, selectedGroups, sourceType, title, id]);

  const sourceTypeChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSourceType(value);
  }, []);

  const onNumericValuesChange = useCallback((values: NameIdObj[], type: string) => {
    const ids = values.map((v) => v.id);
    if (type == 'consultants') {
      setSelectedUsers(ids);
    }

    if (type == 'groups') {
      setSelectedGroups(ids);
    }
  }, []);

  const handleCancel = () => {
    setTitle(titleProp);
    setSourceType(sourceTypeProp);
    if (selectedIds) {
      if (sourceTypeProp == 'consultants') {
        setSelectedUsers(selectedIds.split(',').map(Number));
      }

      if (sourceTypeProp == 'groups') {
        setSelectedGroups(selectedIds.split(',').map(Number));
      }
    }
    setShowConfigDialog(false);
  };

  return (
    <>
      <Dialog open={showConfigDialog} fullWidth>
        <DialogTitle>Configure Element</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt="10px">
            <RWTextFieldComponent
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              select
              label="Source Type"
              value={sourceType}
              onChange={sourceTypeChangeHandler}
              fullWidth
            >
              <MenuItem value="company">All</MenuItem>
              <MenuItem value="consultants">Consultant</MenuItem>
              <MenuItem value="groups">Group</MenuItem>
              <MenuItem value="currentuser">Current User</MenuItem>
            </TextField>
            {sourceType === 'consultants' && (
              <MultipleUserPicker
                value={selectedUsers}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'consultants');
                }}
                label="Consultants"
              />
            )}
            {sourceType === 'groups' && (
              <MultipleGroupPicker
                value={selectedGroups}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'groups');
                }}
                label="Groups"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={onSaveConfig}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showConfigButton && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 30,
            zIndex: (t) => t.zIndex.appBar - 5,
            color: (t) => t.palette.text.disabled,
          }}
          onClick={() => setShowConfigDialog(true)}
        >
          <SettingsIcon />
        </IconButton>
      )}
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" padding={1} textAlign="center" fontWeight={600} component="div">
          {title}
        </Typography>

        {isLoading && errorHandler == '' && (
          <Box m="auto">
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (
          <div style={{ height: 'calc(100% - 48px)' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart width={150} height={150} data={data ?? []} margin={{ top: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="stage" stroke={t.palette.text.primary} />
                <YAxis stroke={t.palette.text.primary} />
                <Bar dataKey="total" fill={t.palette.primary.main}>
                  <LabelList dataKey="total" position="top" fill={t.palette.text.primary} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {errorHandler != '' && (
          <Typography
            variant="subtitle2"
            padding={1}
            textAlign="center"
            fontWeight={600}
            component="div"
          >
            {errorHandler}
          </Typography>
        )}
      </Paper>
    </>
  );
}
